
export default function FeatureComparisonSection() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Professional vs DIY <span className="text-teal-600">Website Comparison</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Many El Paso business owners consider building their own website to save money. Here's an honest 
            comparison of DIY platforms versus professional web design services.
          </p>
        </div>

        <div className="overflow-x-auto mb-16">
          <table className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-lg font-semibold text-gray-900">Features</th>
                <th className="px-6 py-4 text-center text-lg font-semibold text-red-600">DIY Platforms</th>
                <th className="px-6 py-4 text-center text-lg font-semibold text-teal-600">Professional Design</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 font-medium text-gray-900">Design Quality</td>
                <td className="px-6 py-4 text-center text-red-600">Template-based, generic</td>
                <td className="px-6 py-4 text-center text-teal-600">Custom, unique design</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">Time to Complete</td>
                <td className="px-6 py-4 text-center text-red-600">Weeks to months</td>
                <td className="px-6 py-4 text-center text-teal-600">1-2 weeks</td>
              </tr>
              <tr>
                <td className="px-6 py-4 font-medium text-gray-900">SEO Optimization</td>
                <td className="px-6 py-4 text-center text-red-600">Basic, limited</td>
                <td className="px-6 py-4 text-center text-teal-600">Comprehensive, advanced</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">Mobile Responsiveness</td>
                <td className="px-6 py-4 text-center text-red-600">May have issues</td>
                <td className="px-6 py-4 text-center text-teal-600">Perfect across all devices</td>
              </tr>
              <tr>
                <td className="px-6 py-4 font-medium text-gray-900">Technical Support</td>
                <td className="px-6 py-4 text-center text-red-600">Limited, generic help</td>
                <td className="px-6 py-4 text-center text-teal-600">Personal, expert support</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">Loading Speed</td>
                <td className="px-6 py-4 text-center text-red-600">Often slow</td>
                <td className="px-6 py-4 text-center text-teal-600">Optimized for speed</td>
              </tr>
              <tr>
                <td className="px-6 py-4 font-medium text-gray-900">Ongoing Costs</td>
                <td className="px-6 py-4 text-center text-red-600">Monthly subscriptions</td>
                <td className="px-6 py-4 text-center text-teal-600">One-time investment</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              The Hidden Costs of DIY Websites
            </h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              While DIY website builders advertise low monthly costs, the true expense comes from lost opportunities. 
              Poor design, slow loading times, and weak SEO mean fewer customers find and trust your business. 
              Many El Paso businesses spend months trying to perfect their DIY site, taking valuable time away 
              from running their business.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Professional web design is an investment that pays for itself through increased customers, higher 
              conversion rates, and improved brand credibility. Your website represents your business 24/7—it 
              should reflect the quality and professionalism of your services.
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h4 className="text-xl font-semibold text-red-800 mb-3">Common DIY Pitfalls:</h4>
              <ul className="space-y-2 text-red-700">
                <li className="flex items-center space-x-2">
                  <i className="ri-close-circle-line text-red-500"></i>
                  <span>Generic templates that look like competitors</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-close-circle-line text-red-500"></i>
                  <span>Poor mobile experience losing mobile customers</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-close-circle-line text-red-500"></i>
                  <span>Weak SEO keeping you invisible in searches</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-close-circle-line text-red-500"></i>
                  <span>Slow loading times frustrating visitors</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/9bd7dc803b463cc58903eebac4f0a3c4.jpg"
              alt="Business owner struggling with DIY website platforms"
              className="w-full h-80 object-cover rounded-2xl shadow-xl"
            />
          </div>
        </div>

        <div className="bg-teal-50 rounded-2xl p-12">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Why El Paso Businesses Choose Professional Design
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Successful business owners understand that their website is too important to leave to chance. 
              Here's what our El Paso clients value most about professional web design.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Save Time</h4>
              <p className="text-gray-600 leading-relaxed">
                Focus on running your business while experts handle your website. Professional design saves 
                dozens of hours compared to DIY attempts.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-trophy-line text-xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Professional Results</h4>
              <p className="text-gray-600 leading-relaxed">
                Custom design, advanced functionality, and expert optimization deliver results that DIY 
                platforms simply cannot match.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Expert Support</h4>
              <p className="text-gray-600 leading-relaxed">
                Get personalized help from local El Paso experts who understand your market and can 
                provide ongoing assistance when needed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
